import { useEffect, useState } from "react";
import { Pencil, Trash2, Printer } from "lucide-react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, fmtDate, fmtDateTime, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { RagBadge } from "./Badges";

export const AreaDetail = ({ area, onClose, onEdit, onDelete }) => {
  const { isManager } = useAuth();
  const [assets, setAssets] = useState([]);
  const [audits, setAudits] = useState([]);
  useEffect(() => {
    if (!area) return;
    api.get("/assets", { params: { area_id: area.id } }).then((r) => setAssets(r.data.sort((a, b) => (a.route_order ?? 9999) - (b.route_order ?? 9999))));
    api.get(`/areas/${area.id}/audits`).then((r) => setAudits(r.data));
  }, [area]);
  return (
    <Dialog open={!!area} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-700 bg-ink-2 sm:max-w-2xl" data-testid="area-detail-dialog">
        {area && (
          <>
            <DialogHeader>
              <p className="font-mono text-xs text-cyan-300">{area.code}</p>
              <DialogTitle className="font-display text-2xl uppercase">{area.name}</DialogTitle>
            </DialogHeader>
            {area.description && <p className="text-sm text-slate-400">{area.description}</p>}
            <div className="flex gap-2 overflow-x-auto">
              {(area.photos || []).map((p) => <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer"><img src={fileUrl(p)} alt={area.name} className="h-32 w-48 shrink-0 rounded object-cover" /></a>)}
              {!area.photos?.length && <p className="text-xs text-slate-500">No area photos yet.</p>}
            </div>
            {isManager && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="border-slate-700 bg-transparent" onClick={onEdit} data-testid="area-edit-btn"><Pencil className="mr-1 h-3.5 w-3.5" />Edit area</Button>
                <Button size="sm" variant="ghost" className="text-red-300" onClick={onDelete} data-testid="area-delete-btn"><Trash2 className="mr-1 h-3.5 w-3.5" />Delete</Button>
              </div>
            )}
            <div className="flex items-center gap-4 rounded border border-slate-800 p-3" data-testid="area-qr">
              <div className="rounded bg-white p-2"><QRCodeSVG value={`AREA:${area.id}`} size={88} /></div>
              <div className="text-sm">
                <p className="font-semibold">Check-in QR</p>
                <p className="text-xs text-slate-400">Auditors scan this on arrival.{area.pin ? " Pinned on site plan." : " Not pinned on a site plan yet."}</p>
                <Link to={`/area-labels?area=${area.id}`} className="mt-1 inline-flex items-center gap-1 text-xs text-rose-300 underline" data-testid="area-print-label-link"><Printer className="h-3 w-3" />Print label</Link>
              </div>
            </div>
            <p className="eyebrow">Assets on the route (in order)</p>
            <div className="space-y-1">
              {assets.map((a, i) => (
                <div key={a.id} className="flex items-center justify-between rounded border border-slate-800 px-3 py-2 text-sm" data-testid={`area-asset-${a.asset_code}`}>
                  <span><span className="mr-2 font-mono text-cyan-300">{i + 1}. {a.asset_code}</span>{a.name}<span className="ml-2 text-xs text-slate-500">{a.type}</span></span>
                  <RagBadge status={a.rag} />
                </div>
              ))}
              {!assets.length && <p className="text-xs text-slate-500">No assets yet. Set the "Area" field when adding or editing an asset in any register.</p>}
            </div>
            <p className="eyebrow">Recent pathway audits for this area</p>
            <div className="space-y-2">
              {audits.map((au) => (
                <div key={au.id} className="rounded border border-slate-800 p-3 text-sm" data-testid={`area-audit-${au.id}`}>
                  <div className="flex items-center justify-between">
                    <span>{au.template_name}<span className="block text-xs text-slate-500">{fmtDateTime(au.performed_at)} - {au.performed_by}{au.checkin ? ` - checked in ${au.checkin.method === "qr" ? "by QR" : "manually"}` : " - no check-in"}</span></span>
                    <RagBadge status={au.area_fails ? "red" : "green"} label={au.area_fails ? `${au.area_fails} fail` : "Pass"} />
                  </div>
                  {au.results.filter((r) => r.result === "fail").map((r, i) => <p key={i} className="mt-1 text-xs text-red-300">{r.asset_name || "General"}: {r.item}{r.note ? ` - ${r.note}` : ""}</p>)}
                </div>
              ))}
              {!audits.length && <p className="text-xs text-slate-500">Not audited yet{area.last_audit_at ? "" : ""}. Last audit: {fmtDate(area.last_audit_at)}</p>}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
